import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, HStack, Spinner, Text, textVariants, VStack } from 'ui_2';
import { Route as game_route } from '@routes/game';
import { useMemo } from 'react';
import { connect, useConnect } from '@puzzlehq/sdk';
import {
  useAleoBalance,
  useFeeQuery,
  useHasInjectedConnection,
} from '@puzzlehq/frontend-utils';
import { FeeView, FeeViewBanner } from '@src/components/FeeView';
import { usePuzzleGameContext } from '@src/providers/puzzleProvider';
import { Header } from '@src/components/Header';
import { IncorrectNetworkModal } from '@src/components/IncorrectNetworkDialog';
import { useState } from 'react';
import { useEffect } from 'react';
import { useGameStore } from '@src/data/useGameStore';
import { connectRequest, MYSTERY_CITY_AUDIO_BASE_URL } from '@src/data/utils';
import { BiLinkExternal } from 'react-icons/bi';
import useWindowDimensions from '@puzzlehq/frontend-utils/src/hooks/useWindowDimensions';

const introSound = `${MYSTERY_CITY_AUDIO_BASE_URL}/intro.mp3`;

// Create a single audio instance outside the component
const introAudio = new Audio(introSound);
introAudio.loop = true;
introAudio.volume = 0.2;

// eslint-disable-next-line
export const Route = createFileRoute('/')({
  beforeLoad: async () => {
    try {
      const connectResponse = await connect(connectRequest);
      console.log(`/ connectResponse ${JSON.stringify(connectResponse)}`);
    } catch (e) {
      console.error(`Error calling connect on /`, connectRequest, e);
    }
  },
  component: function Component() {
    const [playLoading, setPlayLoading] = useState(false);
    const navigate = useNavigate({ from: Route.id });
    const {
      connect,
      isConnected,
      loading: connectionLoading,
    } = useConnect(connectRequest);

    const { hasInjectedConnection, isLoading: injectedLoading } =
      useHasInjectedConnection();

    const { parcBalance } = usePuzzleGameContext();
    const { totalBalance } = useAleoBalance();

    const feeQuery = useFeeQuery({
      programId: import.meta.env.VITE_MYSTERY_CITY_CONTRACT!,
      functionId: 'submit_game',
      network: import.meta.env.VITE_NETWORK!,
    });

    const fee = feeQuery.data?.fee?.fee.total;
    const covered = feeQuery.data?.covered;

    const lowAleoBalance =
      fee !== undefined &&
      totalBalance !== undefined &&
      covered === false &&
      totalBalance < fee;
    const lowParcBalance = parcBalance !== undefined && parcBalance < 1;

    const button = useMemo(() => {
      if (hasInjectedConnection === false && injectedLoading === false)
        return (
          <>
            <Text family='space' color='aqua' className='bg-black'>
              You must have Puzzle Wallet to play.
            </Text>
            <Button
              variant='mysteryCityPrimary'
              onClick={() => {
                window.open('https://puzzle.online', '_blank');
              }}
              disabled={connectionLoading}
              className={textVariants({
                className: 'items-center gap-2',
                family: 'space',
                color: 'aqua',
                font: 'medium-lg',
              })}
              size='lg'
            >
              Download Puzzle <BiLinkExternal />
            </Button>
          </>
        );
      else if (!isConnected)
        return (
          <Button
            variant='mysteryCityPrimary'
            onClick={() => {
              connect();
            }}
            disabled={connectionLoading}
            className={textVariants({
              className: 'gap-2',
              family: 'space',
              color: 'aqua',
              font: 'medium-lg',
            })}
            size='lg'
          >
            Connect Wallet
            {connectionLoading && <Spinner className='text-white' />}
          </Button>
        );
      else {
        return (
          <>
            {(() => {
              if (lowAleoBalance)
                return (
                  <Text family='space' color='aqua' className='bg-black'>
                    Your Aleo Balance is too low to play.
                  </Text>
                );
              else if (lowParcBalance)
                return (
                  <Text family='space' color='aqua' className='bg-black'>
                    Your PARC Balance is too low to play.
                  </Text>
                );
            })()}
            <Button
              variant='mysteryCityPrimary'
              className='mb-10 h-14 gap-2 lg:mb-0'
              disabled={lowAleoBalance || lowParcBalance || playLoading}
              onClick={async () => {
                setPlayLoading(true);
                await navigate({ to: game_route.to }).catch((e) => {
                  console.error('Error navigating to game_route', e);
                  setPlayLoading(false);
                });
              }}
            >
              <VStack className='justify-center'>
                <HStack className='items-center justify-center gap-2'>
                  <Text family='space' color='aqua' font='medium-lg'>
                    {playLoading ? 'Loading' : 'Play'}
                  </Text>
                  {playLoading && <Spinner />}
                </HStack>
                <Text family='space' color='aqua'>
                  Costs 1 PARC
                </Text>
              </VStack>
            </Button>
            <div className='hidden lg:flex'>
              <FeeView />
            </div>
          </>
        );
      }
    }, [
      isConnected,
      navigate,
      connectionLoading,
      lowAleoBalance,
      lowParcBalance,
      playLoading,
      hasInjectedConnection,
      injectedLoading,
    ]);

    const [videoReady, setVideoReady] = useState(false);
    useEffect(() => {
      const videoElement = document.getElementById(
        'intro-video'
      ) as HTMLVideoElement | null;

      const handleCanPlay = () => {
        setVideoReady(true);
      };

      if (videoElement) {
        videoElement.addEventListener('canplay', handleCanPlay);
      }

      return () => {
        if (videoElement) {
          videoElement.removeEventListener('canplay', handleCanPlay);
        }
      };
    }, []);

    useEffect(() => {
      const handleVisibilityChange = () => {
        const videoElement = document.getElementById(
          'intro-video'
        ) as HTMLVideoElement | null;
        if (document.visibilityState === 'visible' && videoElement) {
          videoElement.play();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
      };
    }, []);

    const [isMuted] = useGameStore((state) => [state.isMuted]);

    useEffect(() => {
      let playingOnHide = false;

      const handleVisibilityChange = () => {
        if (document.hidden) {
          playingOnHide = !introAudio.paused;
          introAudio.pause();
        } else if (playingOnHide && !isMuted) {
          introAudio.play().catch(console.error);
        }
      };

      if (!isMuted) {
        introAudio.play().catch(console.error);
      } else {
        introAudio.pause();
      }

      const playAudio = () => {
        if (!isMuted) {
          introAudio.play().catch(console.error);
        }
        document.removeEventListener('click', playAudio);
      };

      document.addEventListener('click', playAudio);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        introAudio.pause();
        document.removeEventListener('click', playAudio);
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
      };
    }, [isMuted]);

    const { width } = useWindowDimensions();
    const isMobile = width < 800;

    return (
      <VStack className='relative h-full flex-none'>
        <div className='relative'>
          <video
            id='intro-video'
            playsInline
            autoPlay={true}
            loop={true}
            controls={false}
            muted={true}
            src={
              isMobile
                ? 'https://storage.googleapis.com/bucket-quickstart_jigsaw-bigshot/MysteryCity/videos/intro_mobile.mp4'
                : 'https://storage.googleapis.com/bucket-quickstart_jigsaw-bigshot/MysteryCity/videos/intro_desktop.mp4'
            }
            className='h-[100dvh] w-screen object-cover'
          />
          {!videoReady && (
            <img
              src={
                isMobile
                  ? 'https://storage.googleapis.com/bucket-quickstart_jigsaw-bigshot/MysteryCity/images/intro_mobile.png'
                  : 'https://storage.googleapis.com/bucket-quickstart_jigsaw-bigshot/MysteryCity/images/intro_desktop.png'
              }
              alt='Video placeholder'
              className='absolute top-0 h-[100dvh] w-screen object-cover'
            />
          )}
        </div>
        <Header showLogo={true} />

        <VStack className='absolute bottom-10 left-1/2 w-full max-w-[300px] -translate-x-1/2 transform justify-center gap-2 text-center'>
          {button}
        </VStack>
        {isConnected && (
          <div className='absolute bottom-0 w-screen lg:hidden'>
            <FeeViewBanner />
          </div>
        )}
        {isConnected && <IncorrectNetworkModal />}
      </VStack>
    );
  },
});
